import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, AlertCircle, Volume2 } from 'lucide-react';
import RevokedMessageNotice from './RevokedMessageNotice';

const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default function VoiceMessagePlayer({
  audioUrl,
  duration = 0,
  isSender = false,
  isRevoked = false,
  revokedReason,
  violations = []
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const [loadError, setLoadError] = useState(false);

  const audioRef = useRef(null);

  // If revoked, do not render audio player or load audio
  if (isRevoked) {
    return (
      <RevokedMessageNotice
        reason={revokedReason}
        violations={violations}
        isSender={isSender}
      />
    );
  }

  const togglePlay = () => {
    if (!audioRef.current || loadError) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(err => {
        console.error('Audio playback error:', err);
        setLoadError(true);
        setIsPlaying(false);
      });
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current && (!audioDuration || audioDuration === 0)) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e) => {
    const seekTime = parseFloat(e.target.value);
    setCurrentTime(seekTime);
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
    }
  };

  const displayDuration = audioDuration || duration || 0;
  const progressPercent = displayDuration > 0 ? (currentTime / displayDuration) * 100 : 0;

  return (
    <div
      data-testid="voice-message-player"
      className={`px-4 py-2.5 rounded-lg flex items-center space-x-3 w-64 sm:w-72 select-none ${
        isSender
          ? 'bg-blue-600 text-white'
          : 'bg-white border border-gray-200 text-gray-900 shadow-sm'
      }`}
    >
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={() => setLoadError(true)}
        className="hidden"
      />

      {/* Play/Pause Button */}
      <button
        type="button"
        onClick={togglePlay}
        disabled={loadError || !audioUrl}
        data-testid="voice-play-pause-btn"
        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-transform active:scale-95 ${
          isSender
            ? 'bg-white text-blue-600 hover:bg-blue-50'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        aria-label={isPlaying ? 'Pause voice message' : 'Play voice message'}
      >
        {loadError ? (
          <AlertCircle className="w-4 h-4 text-red-500" />
        ) : isPlaying ? (
          <Pause className="w-4 h-4 fill-current" />
        ) : (
          <Play className="w-4 h-4 fill-current ml-0.5" />
        )}
      </button>

      {/* Waveform / Progress Slider */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="relative w-full flex items-center h-4">
          <input
            type="range"
            min={0}
            max={displayDuration || 1}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            disabled={loadError || !audioUrl}
            className={`w-full h-1.5 rounded-lg appearance-none cursor-pointer focus:outline-none ${
              isSender
                ? 'accent-white bg-blue-400'
                : 'accent-blue-600 bg-gray-200'
            }`}
          />
        </div>

        {/* Time and Status */}
        <div
          className={`flex justify-between items-center text-[11px] font-mono mt-0.5 ${
            isSender ? 'text-blue-100' : 'text-gray-500'
          }`}
        >
          <span>{formatDuration(isPlaying ? currentTime : displayDuration)}</span>
          <span className="flex items-center space-x-1">
            <Volume2 className="w-3 h-3 opacity-75" />
            <span className="text-[10px] font-sans uppercase font-medium">Voice</span>
          </span>
        </div>
      </div>
    </div>
  );
}
